/**
 * CLI device-flow approval page (WARD-65).
 *
 * Landed on by the user after running `wardkey login` — the CLI prints
 * a URL of the form `/cli/approve?user_code=ABCD-EF23`. This page:
 *
 *   1. Reads the user_code from the query string.
 *   2. Looks it up via /bff/cli/lookup (public — the user_code is the auth).
 *   3. Shows the requesting CLI's label + scopes, plus the user_code that
 *      should match what the CLI printed (defends against open-redirect-y
 *      cases where the URL was tampered with).
 *   4. On Approve, POSTs to /bff/cli/approve which calls the API with the
 *      session attached. The CLI's next poll then mints a token.
 *
 * The page is session-protected via the standard loader pattern: if the
 * user isn't signed in, requireAuth redirects to /login with return_to
 * set so they come back here after auth.
 */

import { Button } from "@/components/ui/button";
import { requireAuth } from "@/lib/auth";
import { type FormEvent, useEffect, useState } from "react";
import {
  type LoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs,
  useLoaderData,
  useSearchParams,
} from "react-router";

type Loader = {
  user_code: string | null;
};

export async function cliApproveLoader(args: LoaderFunctionArgs): Promise<Loader> {
  // requireAuth bounces to /login?return_to=… on a missing/expired session.
  await requireAuth(args);
  const url = new URL(args.request.url);
  return { user_code: url.searchParams.get("user_code") };
}

export function cliApproveShouldRevalidate(_args: ShouldRevalidateFunctionArgs): boolean {
  return false;
}

type LookupBody = {
  user_code: string;
  state: "pending" | "approved" | "consumed" | "denied" | "expired";
  client_label: string | null;
  scopes: string[];
  expires_at: number;
};

type LookupState =
  | { stage: "loading" }
  | { stage: "ready"; body: LookupBody }
  | { stage: "missing_code" }
  | { stage: "not_found" }
  | { stage: "error"; message: string };

type ApproveState =
  | { stage: "idle" }
  | { stage: "submitting" }
  | { stage: "approved"; client_label: string | null }
  | { stage: "error"; message: string };

const TERMINAL_LABELS: Record<LookupBody["state"], string> = {
  pending: "",
  approved: "This code has already been approved by another browser session.",
  consumed: "This code has already been consumed by the CLI.",
  denied: "This code was denied.",
  expired: "This code has expired. Run `wardkey login` again to get a fresh one.",
};

export function CliApproveRoute() {
  const { user_code } = useLoaderData() as Loader;
  const [searchParams] = useSearchParams();
  const liveCode = user_code ?? searchParams.get("user_code");

  const [lookup, setLookup] = useState<LookupState>(
    liveCode ? { stage: "loading" } : { stage: "missing_code" },
  );
  const [approve, setApprove] = useState<ApproveState>({ stage: "idle" });

  useEffect(() => {
    if (!liveCode) return;
    const ctrl = new AbortController();
    void fetch(`/bff/cli/lookup?user_code=${encodeURIComponent(liveCode)}`, {
      signal: ctrl.signal,
    })
      .then(async (res) => {
        if (res.status === 404) {
          setLookup({ stage: "not_found" });
          return;
        }
        if (!res.ok) {
          setLookup({ stage: "error", message: `Lookup failed: HTTP ${res.status}` });
          return;
        }
        const body = (await res.json()) as LookupBody;
        setLookup({ stage: "ready", body });
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === "AbortError") return;
        setLookup({ stage: "error", message: (err as Error)?.message ?? "Lookup failed" });
      });
    return () => ctrl.abort();
  }, [liveCode]);

  async function onApprove(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!liveCode) return;
    setApprove({ stage: "submitting" });
    try {
      const res = await fetch("/bff/cli/approve", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ user_code: liveCode }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const body = (await res.json()) as {
            error?: { message?: string };
            error_message?: string;
          };
          if (body?.error?.message) detail = body.error.message;
        } catch {
          // body wasn't JSON; keep the status string.
        }
        setApprove({ stage: "error", message: detail });
        return;
      }
      const body = (await res.json()) as { client_label: string | null };
      setApprove({ stage: "approved", client_label: body.client_label });
    } catch (err) {
      setApprove({ stage: "error", message: (err as Error)?.message ?? "Approve failed" });
    }
  }

  if (lookup.stage === "missing_code") {
    return (
      <Layout eyebrow="missing code">
        <Heading>Missing code</Heading>
        <Body>
          This page expects a <Code>user_code</Code> query parameter. Re-run{" "}
          <Code>wardkey login</Code> and follow the URL it prints.
        </Body>
      </Layout>
    );
  }
  if (lookup.stage === "loading") {
    return (
      <Layout eyebrow="device authorization">
        <Heading>Looking up authorization request…</Heading>
      </Layout>
    );
  }
  if (lookup.stage === "not_found") {
    return (
      <Layout eyebrow="code not found">
        <Heading>Code not found</Heading>
        <Body>
          That code is unknown or has expired. Run <Code>wardkey login</Code> again.
        </Body>
      </Layout>
    );
  }
  if (lookup.stage === "error") {
    return (
      <Layout eyebrow="lookup failed">
        <Heading>Lookup failed</Heading>
        <Body>{lookup.message}</Body>
      </Layout>
    );
  }

  const { body } = lookup;
  if (body.state !== "pending") {
    return (
      <Layout eyebrow="unavailable">
        <Heading>This code can't be approved</Heading>
        <Body>{TERMINAL_LABELS[body.state]}</Body>
      </Layout>
    );
  }

  if (approve.stage === "approved") {
    return (
      <Layout eyebrow="cli authorized">
        <Heading>CLI authorized</Heading>
        <Body>
          <strong className="font-medium text-foreground">
            {approve.client_label ?? "wardkey-cli"}
          </strong>{" "}
          can now access your account. You can close this tab — the CLI will pick up the token on
          its next poll.
        </Body>
      </Layout>
    );
  }

  return (
    <Layout eyebrow="authorize device">
      <Heading>Authorize CLI access</Heading>
      <Body>
        A device is asking to sign in as you. Confirm the code matches the one your terminal showed.
      </Body>
      <dl className="mt-6 divide-y divide-border border-y border-border">
        <Row label="Device">{body.client_label ?? "(unlabeled)"}</Row>
        <Row label="User code">
          <Code>{body.user_code}</Code>
        </Row>
        <Row label="Scopes">{body.scopes.length === 0 ? "(none)" : body.scopes.join(", ")}</Row>
      </dl>
      <form onSubmit={onApprove} className="mt-6">
        <Button type="submit" disabled={approve.stage === "submitting"}>
          {approve.stage === "submitting" ? "Approving…" : "Approve"}
        </Button>
      </form>
      {approve.stage === "error" ? (
        <p role="alert" className="mt-4 text-[13px] text-destructive">
          {approve.message}
        </p>
      ) : null}
    </Layout>
  );
}

function Layout(props: { eyebrow: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-lg items-center justify-center px-4">
      <main className="w-full rounded-xl border border-border bg-card p-8 shadow-[0_8px_30px_rgba(0,0,0,0.4)]">
        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {props.eyebrow}
        </div>
        {props.children}
      </main>
    </div>
  );
}

function Heading(props: { children: React.ReactNode }) {
  return (
    <h1 className="mt-4 font-sans text-2xl font-medium tracking-tight text-foreground">
      {props.children}
    </h1>
  );
}

function Body(props: { children: React.ReactNode }) {
  return <p className="mt-3 text-[14px] leading-relaxed text-muted-foreground">{props.children}</p>;
}

function Code(props: { children: React.ReactNode }) {
  return (
    <code className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[0.85em] text-foreground">
      {props.children}
    </code>
  );
}

function Row(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4 py-2.5">
      <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
        {props.label}
      </dt>
      <dd className="m-0 min-w-0 text-[14px] text-foreground">{props.children}</dd>
    </div>
  );
}
